/* The editing commands for the selected copies (0.3e). One place for everything that changes the
   selection, so the Selected design panel, the quick toolbar, the right-click menu and the phone edit bar
   all run exactly the same code. Each change is one undo step (commit / updateItems) and autosaves as
   usual; the price and the checks follow from the changed gangsheet. */

import { type Item } from '@gsb/core';
import {
  useStore, updateItems, clampInside, round1, aspectOf, duplicateSelected, deleteSelected, layer, setSideHidden, setLockAspect
} from '../store/store';

const get = useStore.getState;

/** the copies selected right now (any sheet) */
export function selectedItems(ids: string[] = get().selection): Item[] {
  return get().doc.sheets.flatMap(s => s.items).filter(i => ids.includes(i.id));
}

/** how long a by-the-metre sheet may grow (copies are kept inside it) */
function maxLength(): number {
  const s = get();
  return s.config?.pricing.transfers[s.doc.transfer].byLength.maxLengthMm ?? 5000;
}

/** Width and/or height (mm). With Lock proportions on, the other side follows this copy's proportions. */
export function resizeSelection(ids: string[], w: number | null, h: number | null) {
  const lock = get().lockAspect, maxL = maxLength();
  updateItems('Resize', ids, (it, s) => {
    const ar = it.widthMm / it.heightMm;
    if (w !== null) { it.widthMm = round1(w); if (lock) it.heightMm = round1(w / ar); }
    if (h !== null) { it.heightMm = round1(h); if (lock) it.widthMm = round1(h * ar); }
    const p = clampInside(it, s, maxL); it.cx = p.cx; it.cy = p.cy;
  });
}

/** Position of the copy's outline from the sheet's top-left (mm). */
export function moveSelection(ids: string[], x: number | null, y: number | null, box: { w: number; h: number }) {
  const maxL = maxLength();
  updateItems('Move', ids, (it, s) => {
    if (x !== null) it.cx = x + box.w / 2;
    if (y !== null) it.cy = y + box.h / 2;
    const p = clampInside(it, s, maxL); it.cx = p.cx; it.cy = p.cy;
  });
}

/** Turn by some degrees, or to an angle. */
export function rotateSelection(ids: string[], by: number | null, to?: number) {
  const maxL = maxLength();
  updateItems('Rotate', ids, (it, s) => {
    it.rotation = (((to ?? it.rotation + (by || 0)) % 360) + 360) % 360;
    const p = clampInside(it, s, maxL); it.cx = p.cx; it.cy = p.cy;
  });
}

export function flipSelection(ids: string[], axis: 'x' | 'y') {
  updateItems('Flip', ids, it => { if (axis === 'x') it.flipX = !it.flipX; else it.flipY = !it.flipY; });
}

export function toggleLockPosition(ids: string[]) {
  const locked = selectedItems(ids).every(i => i.locked);
  updateItems(locked ? 'Unlock' : 'Lock', ids, it => { it.locked = !locked; });
}

/* ------------------------------------------------------------ reset */

/** the artwork's own width ÷ height for a copy (null until the file is checked) */
export function artworkRatio(it: Pick<Item, 'designId'>): number | null {
  const s = get();
  const d = s.doc.designs.find(x => x.id === it.designId);
  if (!d || s.assets[d.assetId]?.status !== 'ready') return null;
  return aspectOf(d);
}
/** this copy's proportions ÷ the artwork's (1 = as drawn) */
export function stretchOf(it: Item): number {
  const r = artworkRatio(it);
  return r ? (it.widthMm / it.heightMm) / r : 1;
}
export const isStretched = (it: Item) => Math.abs(stretchOf(it) - 1) > 0.01;
export const isTurned = (it: Item) => { const r = ((it.rotation % 360) + 360) % 360; return r > 0.01 && r < 359.99; };

/** what Reset can do for these copies */
export function resetState(ids: string[] = get().selection) {
  const items = selectedItems(ids).filter(i => !i.locked);
  return { proportions: items.some(isStretched), rotation: items.some(isTurned) };
}

/**
 * Put the selected copies back to the artwork's proportions (the width stays, the height follows), to 0°,
 * or both. Only the selected copies change; other copies of the design, flips and the artwork itself are
 * left alone. One undo step.
 */
export function resetSelection(ids: string[], what: { proportions?: boolean; rotation?: boolean }) {
  const label = what.proportions && what.rotation ? 'Reset design' : what.proportions ? 'Reset proportions' : 'Reset rotation';
  // only copies that actually need it change (a copy already in proportion keeps its exact size)
  const ratios = new Map(selectedItems(ids).filter(isStretched).map(i => [i.id, artworkRatio(i)]));
  const maxL = maxLength();
  updateItems(label, ids, (it, s) => {
    if (it.locked) return;
    const r = ratios.get(it.id);
    if (what.proportions && r) it.heightMm = round1(it.widthMm / r);
    if (what.rotation && isTurned(it as Item)) it.rotation = 0;
    const p = clampInside(it, s, maxL); it.cx = p.cx; it.cy = p.cy;
  });
}

/* ------------------------------------------------- the rest, as they are */
export const duplicateSelection = (times = 1) => duplicateSelected(times);
export const deleteSelection = () => deleteSelected();
export const layerSelection = (where: 'front' | 'back' | 'forward' | 'backward') => layer(where);
export const toggleProportionLock = () => setLockAspect(!get().lockAspect);

/**
 * "More options": the Selected design panel. On a phone it's the bottom sheet; on a bigger screen the
 * right-hand panel (unfolded if it was folded away), scrolled to the selected design, its first field
 * focused when `focusField` asks for it.
 */
export function openSelectionPanel(focusField?: 'width') {
  const phone = typeof window !== 'undefined' && window.matchMedia('(max-width: 759px)').matches;
  if (phone) { useStore.setState({ sideOpen: true, panel: null, focus: false }); return; }
  if (get().sideHidden) setSideHidden(false);
  useStore.setState({ focus: false });
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const box = document.querySelector('.aside .sel') as HTMLElement | null;
    box?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    if (focusField === 'width') (box?.querySelector('input') as HTMLInputElement | null)?.focus();
  }));
}
